// src/pages/processifyd/ProcessifydUsers.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faList,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydUsers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // kept name SAME

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [loading, setLoading] = useState(false);

  const initialForm = {
    userId: "",
    userName: "",
    userDepartment: "",
    userLoginId: "",
    userPassword: "",
    userContactNumber: "",
    clientName: "",
    active: "Yes",
  };

  const [formData, setFormData] = useState(initialForm);

  // Sample Demo Data
  const demoUsers = [
    {
      id: 1,
      userId: "USR-001",
      userName: "Sanjeev Kumar",
      userDepartment: "Software Engineering",
      userLoginId: "sanjeevkumarmumbai2@gmail.com",
      userPassword: "12345678",
      userContactNumber: "9876543210",
      clientName: "21st Century Financial Services",
      active: "Yes",
      createdDate: "12/12/23 15:32",
      lastUpdateDate: "12/12/23 15:32",
    },
    {
      id: 2,
      userId: "USR-002",
      userName: "Priya Sharma",
      userDepartment: "Customer Experience",
      userLoginId: "priya.sharma@21cf.com",
      userPassword: "password123",
      userContactNumber: "9123456780",
      clientName: "21st Century Financial Services",
      active: "Yes",
      createdDate: "15/01/24 10:20",
      lastUpdateDate: "10/03/24 14:30",
    },
  ];

  const loadSample = () => {
    setUsers(demoUsers);
  };

  const handleAddNew = () => {
    setEditingUser(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      userId: user.userId || "",
      userName: user.userName || "",
      userDepartment: user.userDepartment || "",
      userLoginId: user.userLoginId || "",
      userPassword: user.userPassword || "",
      userContactNumber: user.userContactNumber || "",
      clientName: user.clientName || "",
      active: user.active || "Yes",
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this user? This action cannot be undone.")) return;
    setUsers((prev) => prev.filter((p) => p.id !== id));
  };

  const handleOpenUser = (user) => {
    navigate(`/processifyd-user/${user.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.userId.trim() || !formData.userName.trim()) {
      alert("User ID and User Name are required.");
      return;
    }

    const newUser = {
      id: editingUser?.id || Date.now(),
      ...formData,
      createdDate: editingUser?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (editingUser) {
      setUsers((prev) =>
        prev.map((p) => (p.id === editingUser.id ? newUser : p))
      );
    } else {
      setUsers((prev) => [...prev, newUser]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = users.filter(
    (p) =>
      p.userName.toLowerCase().includes(search.toLowerCase()) ||
      p.clientName.toLowerCase().includes(search.toLowerCase()) ||
      p.userId.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const [showList, setShowList] = useState(false);
    
  const handleShowMyUsers = async () => {
    try {
      setLoading(true);
      setUsers([]);
      setShowList(false);
    } catch (error) {
      console.error("Error fetching Users:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header SAME */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            👥 Processifyd
          </div>
          <div className="text-xs text-gray-400">
            Users Master Management
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm">⏻</button>
            </>
          ) : (
            <button onClick={() => navigate("/login")} className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md">🔐</button>
          )}
        </div>

        <button className="md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>
      
      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row gap-3">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-56 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              Load Sample Users
            </button>

            <button
              onClick={handleShowMyUsers}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faList} />
              My Users List
            </button>

            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New User
            </button>
          </div>

          {/* Search Filter */}
          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by User ID or User Name or Client Name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>
        </div>

        {/* Users Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((user) => (
            <div
              key={user.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors">
                    {user.userId} : {user.userName}
                  </h3>

                  <div className="text-sm text-gray-400 mt-1">
                    Department:{" "}
                    <span className="text-gray-300">{user.userDepartment}</span>
                  </div>

                  <div className="text-sm text-gray-400 mt-1">
                    Client Name:{" "}
                    <span className="text-cyan-300">{user.clientName}</span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      user.active === "Yes" ? "bg-emerald-600" : "bg-red-600"
                    }`}
                  >
                    {user.active === "Yes" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {/* ACTION BUTTONS — RESTORED */}
              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenUser(user)}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(user)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit User"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(user.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete User"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search
                ? "No user match your search"
                : "No user yet. Click 'New User' or 'Load Sample Users'"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {showList && (
          <div className="mt-4 bg-black text-white rounded-lg p-4">
            {loading ? (
              <p className="text-yellow-200">Loading users...</p>
            ) : users.length === 0 ? (
              <p className="text-yellow-200">No users found.</p>
            ) : (
              <ul className="space-y-2">
                {users.map((usr) => (
                  <li
                    key={usr.id}
                    className="border border-gray-700 rounded p-2"
                  >
                    <p className="font-semibold text-gray-100">{usr.userName}</p>
                    <p className="font-semibold text-gray-100">{usr.clientName}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Add / Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700 max-h-[92vh] overflow-y-auto">
              
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingUser ? "Edit User" : "New User"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white w-12 text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">

                <div>
                  <label className="block text-sm mb-1.5">
                    User ID <span className="text-red-400">*</span>
                  </label>
                  <input
                    required
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Enter User ID"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">
                    User Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    required
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. Sanjeev Kumar"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">
                    User Department <span className="text-red-400">*</span>
                  </label>
                  <input
                    required
                    value={formData.userDepartment}
                    onChange={(e) => setFormData({ ...formData, userDepartment: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. Software Engineering"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">
                    User Login ID <span className="text-red-400">*</span>
                  </label>
                  <input
                    required
                    value={formData.userLoginId}
                    onChange={(e) => setFormData({ ...formData, userLoginId: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. email@domain.com"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">
                    User Login Password <span className="text-red-400">*</span>
                  </label>
                  <input
                    required
                    type="password"
                    value={formData.userPassword}
                    onChange={(e) => setFormData({ ...formData, userPassword: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Enter password"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">
                    User Contact Number
                  </label>
                  <input
                    value={formData.userContactNumber}
                    onChange={(e) => setFormData({ ...formData, userContactNumber: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    placeholder="Optional"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">
                    Client Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    required
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    placeholder="21st Century Financial Services"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">
                    Active <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                    className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                {/* ACTION BUTTONS — RESTORED */}
                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingUser ? "Update User" : "Create User"}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydUsers;